import { IUser } from '@epicuro-next/data/users';
// FIXME boundaries
// tslint:disable-next-line:nx-enforce-module-boundaries
import { isVimeo } from '@epicuro-next/features/vimeo';
import { List } from '@epicuro-next/models/engagement';
import {
  selectIsOpenedListDeleted,
  selectOpenedListPromotedToSpotlight,
} from '@epicuro-next/state/list-entities';
import { selectProductBasketQueryParams } from '@epicuro-next/state/p-basket-state';
import { selectAccountId } from '@epicuro-next/state/session-state';
import {
  selectIsLookbookPromoted,
  selectIsOwnerOfOpenedLookbook,
  selectLookBookInRoute,
} from '@epicuro-next/state/shop-the-look-state';
import { selectUser } from '@epicuro-next/state/user-state';
import { createSelector } from '@ngrx/store';

import {
  adapter,
  productListsFeatureKey,
  State,
} from '../../reducers/lists/product-lists.reducer';
import * as fromProductList from '../../store';
import {
  selectIsOwnerOfOpenedList,
  selectOpenedList,
} from '../list-opened/product-list-opened.selectors';

import {
  selectAllowEditionListsNotOwned,
  selectAllowSpotlightPromotion,
  selectListOldBulkAddStyleIdInputRule,
  selectNotAllowedTypes,
} from './product-lists-configuration-rules.selectors';

/**
 * state slice selector
 */
export const selectProductListsState = createSelector<
  fromProductList.State,
  [fromProductList.ProductListState],
  State
>(
  fromProductList.selectProductListState,
  (state) => state[productListsFeatureKey],
);

const { selectAll, selectTotal } = adapter.getSelectors(
  selectProductListsState,
);

/**
 * Selector to get the ProductLists
 */
export const selectAllProductLists = selectAll;

/**
 * Selector to get the total number of lists
 */
export const selectListsTotal = createSelector(
  selectProductListsState,
  (state: State) => state.total,
);

/**
 * Selector to check if we loaded all the available lists from paging.
 */
export const selectFullLoaded = createSelector(
  selectListsTotal,
  selectTotal,
  (listsTotal: number, total: number) => listsTotal > 0 && listsTotal === total,
);

/**
 * Selector to get the loading state
 */
export const selectIsLoadingProductLists = createSelector(
  selectProductListsState,
  (state: State) => state.loading,
);

/**
 * Selector to get the page size
 */
export const selectPageSize = createSelector(
  selectProductListsState,
  (state: State) => state.size,
);

/**
 * Selector to get the page number
 */
export const selectPage = createSelector(
  selectProductListsState,
  (state: State) => state.page,
);

/**
 * Selector to get the load lists params
 */
export const selectParams = createSelector(
  selectPage,
  selectPageSize,
  (page: number, size: number) => {
    return {
      page,
      size,
    };
  },
);

export const selectProductList = (id: string) =>
  createSelector(selectAllProductLists, (lists: List[]): List | undefined =>
    lists.find((list: List) => list.id === id),
  );

export const selectProductListPatchCompleteQuery = createSelector(
  selectAccountId,
  selectProductBasketQueryParams,
  (accountId, params) => {
    const parameters = Object.entries(params).reduce((aggr, entry) => {
      const paramKey = entry[0];

      let value = entry[1];
      if (Array.isArray(value)) {
        value = value.join(',');
      }
      if (value === undefined || value === null || value === '') {
        return aggr;
      }
      // This is bad implementation as this selector is targeted for old api endpoint
      // /api/v1/productlist/<id>/items/search?accountId=__&f=__&start=__&num=__&sortField=__&sortDirection=__
      // The mapping for presets are normally handled in combined call in gql
      // Only for Relevant endpoint, it needs to be done in here
      // Because it'd take too much effort to centralise
      if (paramKey === 'preset') {
        return `${aggr}&type=${encodeURIComponent(value)}`;
      }

      return `${aggr}&${paramKey}=${encodeURIComponent(value)}`;
    }, '');
    return `accountId=${accountId}${parameters}`;
  },
);

/**
 * Selector to check if current user is owner of product list
 */
export const selectIsOwner = (listId?: string) =>
  createSelector(
    selectUser,
    selectAllProductLists,
    selectOpenedList,
    (user: IUser, lists: List[], openedList: List) => {
      const currentList = lists.find((entry) => entry.id === listId);

      if (!currentList && openedList) {
        return user.externalId === openedList?.creation.user?.externalId;
      }

      return user?.externalId === currentList?.creation.user?.externalId;
    },
  );

/**
 * Selector to get the load list that are not vimeo or youtube
 */
export const selectLookbookCreationProductLists = createSelector(
  selectAllProductLists,
  selectNotAllowedTypes,
  (lists: List[], notAllowedTypes: string[]) => {
    return lists.filter(
      (list) =>
        !(
          notAllowedTypes.includes(list.originalType?.toLowerCase() ?? '') ||
          notAllowedTypes.includes(list.type?.toLowerCase() ?? '') ||
          notAllowedTypes.includes(
            list.mainMedia?.mediaType?.toLowerCase() ?? '',
          )
        ) && !isVimeo(list.mainMedia?.url ?? ''),
    );
  },
);

export const selectListOldBulkAddStyleIdInput = createSelector(
  selectListOldBulkAddStyleIdInputRule,
  (config) => (config ? true : undefined),
);

export const selectIsCurrentUserAllowedToEdit = createSelector(
  selectIsOwnerOfOpenedList,
  selectAllowEditionListsNotOwned,
  selectIsOpenedListDeleted,
  (isOwnerOfOpenedList, isAllowedToEdit, isDeleted) =>
    !isDeleted && (isOwnerOfOpenedList || isAllowedToEdit),
);

export const selectIsShowSpotlightPromotionButton = createSelector(
  selectAllowSpotlightPromotion,
  selectIsOwnerOfOpenedList,
  selectOpenedListPromotedToSpotlight,
  selectLookBookInRoute,
  selectIsOwnerOfOpenedLookbook,
  selectIsLookbookPromoted,
  selectAllowEditionListsNotOwned,
  (
    allowSpotlightPromotion,
    isListCurrentUser,
    isListPromotedToSpotlight,
    isLookbook,
    isLookBookCurrentUser,
    isLookbookPromotedToSpotlight,
    allowEditionListsNotOwned,
  ) => {
    if (isLookbook) {
      return (
        (allowSpotlightPromotion || isLookbookPromotedToSpotlight) &&
        (isLookBookCurrentUser || allowEditionListsNotOwned)
      );
    } else {
      return (
        (allowSpotlightPromotion || isListPromotedToSpotlight) &&
        (isListCurrentUser || allowEditionListsNotOwned)
      );
    }
  },
);
